import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  Group,
  TextInput,
  Button,
  Table,
  Text,
  Select,
  Divider,
  Loader,
  Alert,
  Tooltip,
  Badge,
  ScrollArea,
  Popper,
  Paper,
  Space,
  Switch,
  useMantineTheme,
} from "@mantine/core";
import { useClickOutside, useMediaQuery } from "@mantine/hooks";
import { Plus, Trash, Eye, InfoCircle } from "tabler-icons-react";
import { collection, doc, getDoc, getDocs, query, where, limit, orderBy } from "firebase/firestore";
import { db } from "../../firebase";
import { useFormOptionsSetBuilder } from "../../hooks/useFormOptionsSetBuilder";
import { SubmissionData } from "../../hooks/flexFormUtils";

interface FormOptionsBuilderModalProps {
  opened: boolean;
  onClose: () => void;
  onSave: () => void;
  companyId: string;
}

interface FormField {
  id: string;
  key: string;
  type: string;
  optionsSource?: {
    id?: string;
    collection?: string;
    type?: string;
    options?: Array<{
      label?: string;
      value?: string;
      metadata?: Record<string, any>;
    }>;
  };
}

interface InsertableFieldToken {
  id: string;
  tokenKey: string;
  displayLabel: string;
  tooltip: string;
  isSubProperty?: boolean;
}

interface FormTemplateItem {
  id: string;
  name: string;
  fields: FormField[];
}

interface MetadataItem {
  label: string;
  template: string;
}

export default function FormOptionsBuilderModal({
  opened,
  onClose,
  onSave,
  companyId,
}: FormOptionsBuilderModalProps) {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [templates, setTemplates] = useState<FormTemplateItem[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [name, setName] = useState("");
  const [labelTemplate, setLabelTemplate] = useState("");
  const [valueTemplate, setValueTemplate] = useState("{{id}}");
  const [metadata, setMetadata] = useState<Record<string, MetadataItem>>({});
  const [autoSync, setAutoSync] = useState<boolean>(true);
  const [loadingTemplates, setLoadingTemplates] = useState(true);

  const theme = useMantineTheme();
  const [visible, setVisible] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const clickOutsideRef = useClickOutside<HTMLDivElement>(
    () => setVisible(false),
    null,
    [buttonRef.current].filter((el): el is HTMLButtonElement => el !== null)
  );

  // Focus and Selection tracking
  const [activeInput, setActiveInput] = useState<"label" | "value" | "meta">("label");
  const labelRef = useRef<HTMLInputElement>(null);
  const valueRef = useRef<HTMLInputElement>(null);
  const metaRef = useRef<HTMLInputElement>(null);

  // Metadata editor inputs
  const [metaKey, setMetaKey] = useState("");
  const [metaLabel, setMetaLabel] = useState("");
  const [metaTemplate, setMetaTemplate] = useState("");

  // Preview States
  const [previewSubmissions, setPreviewSubmissions] = useState<SubmissionData[]>([]);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);
  const [previewError, setPreviewError] = useState("");

  const {
    loading: building,
    error: buildError,
    buildOptionsFromSubmissions,
    createOrSyncOptionsSet,
  } = useFormOptionsSetBuilder();

  // Fetch Form Templates
  useEffect(() => {
    if (!opened || !companyId) return;

    setLoadingTemplates(true);
    const q = query(
      collection(db, "flexFormTemplates"),
      where("companyId", "==", companyId),
      where("archived", "==", false)
    );
    getDocs(q)
      .then((snapshot) => {
        const list = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            name: (data.name as string) || "",
            fields: (data.fields as FormField[]) || [],
          };
        });
        setTemplates(list.sort((a, b) => a.name.localeCompare(b.name)));
        setLoadingTemplates(false);
      })
      .catch((err) => {
        console.error("Error loading templates:", err);
        setLoadingTemplates(false);
      });
  }, [opened, companyId]);

  // Selected Template
  const currentTemplate = templates.find((t) => t.id === selectedTemplateId);

  const filteredFields = currentTemplate
    ? currentTemplate.fields.filter(
        (field) =>
          !["gps", "photo", "multiPhoto", "checklist", "checklistWithNotes"].includes(
            field.type
          )
      )
    : [];

  // Fetch recent submissions for preview whenever selectedTemplateId changes
  useEffect(() => {
    if (!selectedTemplateId || !companyId) {
      setPreviewSubmissions([]);
      return;
    }

    setLoadingSubmissions(true);
    setPreviewError("");

    const q = query(
      collection(db, "flexFormSubmissions"),
      where("templateId", "==", selectedTemplateId),
      where("companyId", "==", companyId),
      orderBy("submissionDate", "desc"),
      limit(3)
    );

    getDocs(q)
      .then((snapshot) => {
        if (snapshot.empty) {
          setPreviewSubmissions([]);
          setPreviewError("No submissions found for this template to preview.");
        } else {
          setPreviewSubmissions(
            snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() } as unknown as SubmissionData))
          );
        }
        setLoadingSubmissions(false);
      })
      .catch((err) => {
        console.error("Error loading submissions for preview:", err);
        setPreviewError("Failed to load submissions preview. Ensure you have submissions for this form.");
        setLoadingSubmissions(false);
      });
  }, [selectedTemplateId, companyId]);

  const [referencedOptionSets, setReferencedOptionSets] = useState<Record<string, readonly any[]>>({});

  useEffect(() => {
    if (!currentTemplate || typeof doc !== "function" || typeof getDoc !== "function") {
      setReferencedOptionSets({});
      return;
    }

    const firestoreSources = currentTemplate.fields
      .filter(
        (f) =>
          ["select", "singleSelect", "multiSelect", "multiselect"].includes(f.type) &&
          f.optionsSource?.type === "firestore" &&
          f.optionsSource?.id
      )
      .map((f) => ({
        id: f.optionsSource!.id!,
        collection: f.optionsSource!.collection || "flexFormSelectOptions",
      }));

    if (firestoreSources.length === 0) {
      setReferencedOptionSets({});
      return;
    }

    let cancelled = false;
    Promise.all(
      firestoreSources.map(async (src) => {
        try {
          const snap = await getDoc(doc(db, src.collection, src.id));
          if (snap && typeof snap.exists === "function" && snap.exists()) {
            const data = snap.data() || {};
            return [src.id, Array.isArray(data.options) ? data.options : []] as const;
          }
        } catch {
          // Ignore individual lookup errors
        }
        return [src.id, []] as const;
      })
    ).then((entries) => {
      if (cancelled) return;
      const map: Record<string, readonly any[]> = {};
      for (const [id, opts] of entries) {
        map[id] = opts;
      }
      setReferencedOptionSets(map);
    });

    return () => {
      cancelled = true;
    };
  }, [currentTemplate]);

  const insertableTokens: InsertableFieldToken[] = React.useMemo(() => {
    const tokens: InsertableFieldToken[] = [];

    for (const field of filteredFields) {
      const isSelect = field.type === "select" || field.type === "singleSelect";
      const isMultiSelect = field.type === "multiSelect" || field.type === "multiselect";

      tokens.push({
        id: field.id,
        tokenKey: field.key,
        displayLabel: field.key,
        tooltip: isSelect
          ? `Type: select (Option Value)`
          : isMultiSelect
          ? `Type: multiSelect (Option Values)`
          : `Type: ${field.type}`,
      });

      if (isSelect || isMultiSelect) {
        if (isSelect) {
          tokens.push({
            id: `${field.id}-label`,
            tokenKey: `${field.key}.label`,
            displayLabel: `${field.key}.label`,
            tooltip: `Type: select (Option Label)`,
            isSubProperty: true,
          });
        } else {
          tokens.push({
            id: `${field.id}-labels`,
            tokenKey: `${field.key}_labels`,
            displayLabel: `${field.key}_labels`,
            tooltip: `Type: multiSelect (Option Labels)`,
            isSubProperty: true,
          });
        }

        // Discover nested option metadata keys from template optionsSource, referenced Firestore OptionSets, and preview submissions
        const discoveredMetaKeys = new Set<string>();
        const sourceOptions = [
          ...(Array.isArray(field.optionsSource?.options) ? field.optionsSource!.options! : []),
          ...(field.optionsSource?.id && Array.isArray(referencedOptionSets[field.optionsSource.id])
            ? referencedOptionSets[field.optionsSource.id]
            : []),
        ];

        for (const opt of sourceOptions) {
          if (opt && opt.metadata && typeof opt.metadata === "object") {
            Object.keys(opt.metadata).forEach((k) => {
              if (k !== "label" && k !== "value" && k !== "metadata") {
                discoveredMetaKeys.add(k);
              }
            });
          }
        }

        for (const sub of previewSubmissions) {
          const fieldData = sub.data?.[field.key];
          if (!fieldData) continue;

          const collectFromMeta = (metaObj: any) => {
            if (!metaObj || typeof metaObj !== "object") return;
            const nestedMeta =
              metaObj.metadata && typeof metaObj.metadata === "object"
                ? metaObj.metadata
                : null;
            if (nestedMeta) {
              Object.keys(nestedMeta).forEach((k) => {
                if (k !== "label" && k !== "value" && k !== "metadata") {
                  discoveredMetaKeys.add(k);
                }
              });
            }
            Object.keys(metaObj).forEach((k) => {
              if (k !== "label" && k !== "value" && k !== "metadata") {
                discoveredMetaKeys.add(k);
              }
            });
          };

          if (isSelect) {
            collectFromMeta(fieldData.metadata);
          } else if (isMultiSelect) {
            if (Array.isArray(fieldData.metadata)) {
              for (const metaItem of fieldData.metadata) {
                collectFromMeta(metaItem);
              }
            }
            if (Array.isArray(fieldData.value)) {
              for (const item of fieldData.value) {
                if (item && typeof item === "object") {
                  collectFromMeta(item.metadata || item);
                }
              }
            }
          }
        }

        Array.from(discoveredMetaKeys)
          .sort()
          .forEach((metaKeyName) => {
            tokens.push({
              id: `${field.id}-meta-${metaKeyName}`,
              tokenKey: `${field.key}.${metaKeyName}`,
              displayLabel: `${field.key}.${metaKeyName}`,
              tooltip: `Type: ${field.type} metadata (${metaKeyName})`,
              isSubProperty: true,
            });
          });
      }
    }

    return tokens;
  }, [filteredFields, previewSubmissions, referencedOptionSets]);

  // Generate mapping preview options
  const previewOptions = selectedTemplateId
    ? buildOptionsFromSubmissions(previewSubmissions, {
        labelTemplate,
        valueTemplate,
        metadata,
      })
    : [];

  const handleAddMeta = () => {
    if (!metaKey || !metaLabel || !metaTemplate) return;
    setMetadata({
      ...metadata,
      [metaKey.trim()]: {
        label: metaLabel.trim(),
        template: metaTemplate.trim(),
      },
    });
    setMetaKey("");
    setMetaLabel("");
    setMetaTemplate("");
  };

  const handleRemoveMeta = (key: string) => {
    const copy = { ...metadata };
    delete copy[key];
    setMetadata(copy);
  };

  const handleBuild = async () => {
    if (!name || !selectedTemplateId || !labelTemplate || !valueTemplate) return;

    try {
      const result = await createOrSyncOptionsSet({
        name,
        templateId: selectedTemplateId,
        mappingConfig: {
          labelTemplate,
          valueTemplate,
          metadata,
        },
        companyId,
        autoSync,
      });

      if (result.success) {
        onSave();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const insertVariable = (fieldName: string, target: "label" | "value" | "meta") => {
    const formattedPath = fieldName
      .split(".")
      .map((segment) => (segment.includes("-") || segment.includes(" ") ? `[${segment}]` : segment))
      .join(".");
    const token = `{{${formattedPath}}}`;

    let ref: React.RefObject<HTMLInputElement> | null = null;
    let value = "";
    let setValue: React.Dispatch<React.SetStateAction<string>> | null = null;

    if (target === "label") {
      ref = labelRef;
      value = labelTemplate;
      setValue = setLabelTemplate;
    } else if (target === "value") {
      ref = valueRef;
      value = valueTemplate;
      setValue = setValueTemplate;
    } else if (target === "meta") {
      ref = metaRef;
      value = metaTemplate;
      setValue = setMetaTemplate;
    }

    if (ref && ref.current && setValue) {
      const input = ref.current;
      const currentVal = input.value ?? value;
      const start = input.selectionStart ?? currentVal.length;
      const end = input.selectionEnd ?? currentVal.length;
      const newValue = currentVal.substring(0, start) + token + currentVal.substring(end);
      const newCursorPos = start + token.length;
      input.value = newValue;
      input.setSelectionRange(newCursorPos, newCursorPos);
      setValue(newValue);

      // Refocus and place cursor after inserted token
      setTimeout(() => {
        input.focus();
        input.setSelectionRange(newCursorPos, newCursorPos);
      }, 0);
    } else {
      if (target === "label") {
        setLabelTemplate((prev) => prev + token);
      } else if (target === "value") {
        setValueTemplate((prev) => prev + token);
      } else if (target === "meta") {
        setMetaTemplate((prev) => prev + token);
      }
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Build options set from form submissions"
      size="xl"
      {...({ fullScreen: isMobile } as any)}
    >
      {loadingTemplates ? (
        <Group position="center" my="xl">
          <Loader />
        </Group>
      ) : (
        <div style={{ maxHeight: isMobile ? undefined : "80vh", overflowY: isMobile ? "visible" : "auto", paddingRight: isMobile ? 0 : 10 }}>
          <Group grow mb="md" style={{ flexDirection: isMobile ? "column" : "row" }}>
            <Select
              label="Source Form Template"
              placeholder="Choose template"
              data={templates.map((t) => ({ value: t.id, label: t.name }))}
              value={selectedTemplateId}
              onChange={(val) => {
                if (val) {
                  setSelectedTemplateId(val);
                  // Pre-populate name if empty
                  const t = templates.find((x) => x.id === val);
                  if (t && !name) {
                    setName(`${t.name} Options Set`);
                  }
                }
              }}
              required
              searchable
            />
            <TextInput
              label="Options Set Name"
              placeholder="e.g. Employee Options Set"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </Group>

          {selectedTemplateId && currentTemplate && (
            <>
              <div>
                <Switch
                  checked={autoSync}
                  onChange={(e) => setAutoSync(e.currentTarget.checked)}
                  label="Auto-sync options when submissions are saved, edited, or deleted"
                  mt="sm"
                  data-testid="builder-auto-sync-switch"
                />
                <Text size="xs" color="dimmed" mt={4}>
                  Automatically keeps this option set up to date whenever a submission for the source form is captured or modified.
                </Text>
              </div>
              <Divider my="md" label="Template mappings" labelPosition="center" />

              <Group grow mb="sm">
                <div>
                  <TextInput
                    ref={labelRef}
                    label="Option Label Template"
                    placeholder="e.g. {{first_name}} {{last_name}}"
                    value={labelTemplate}
                    onChange={(e) => setLabelTemplate(e.target.value)}
                    onFocus={() => setActiveInput("label")}
                    required
                    description="The display text for the option."
                  />
                  <Group spacing={4} mt={4}>
                    <Text size="xs" color="dimmed">Quick Insert:</Text>
                    <Badge
                      size="xs"
                      variant="outline"
                      color="gray"
                      style={{ cursor: "pointer" }}
                      onClick={() => insertVariable("submitterName", "label")}
                    >
                      submitterName
                    </Badge>
                    <Badge
                      size="xs"
                      variant="outline"
                      color="gray"
                      style={{ cursor: "pointer" }}
                      onClick={() => insertVariable("documentNumber", "label")}
                    >
                      documentNumber
                    </Badge>
                  </Group>
                </div>
                <div>
                  <TextInput
                    ref={valueRef}
                    label="Option Value Template"
                    placeholder="e.g. {{id}} or {{employee_id}}"
                    value={valueTemplate}
                    onChange={(e) => setValueTemplate(e.target.value)}
                    onFocus={() => setActiveInput("value")}
                    required
                    description="The unique identifier for the option."
                  />
                  <Group spacing={4} mt={4}>
                    <Text size="xs" color="dimmed">Quick Insert:</Text>
                    <Badge
                      size="xs"
                      variant="outline"
                      color="gray"
                      style={{ cursor: "pointer" }}
                      onClick={() => insertVariable("id", "value")}
                    >
                      id
                    </Badge>
                    <Badge
                      size="xs"
                      variant="outline"
                      color="gray"
                      style={{ cursor: "pointer" }}
                      onClick={() => insertVariable("documentNumber", "value")}
                    >
                      documentNumber
                    </Badge>
                  </Group>
                </div>
              </Group>

              {/* Template variables list suggestions */}
              <div style={{ background: "#f8f9fa", padding: 10, borderRadius: 6, marginBottom: 16 }}>
                <Group spacing={6}>
                  <Text size="xs" weight={500}>Fields:</Text>
                  {insertableTokens.map((token) => (
                    <Tooltip key={token.id} label={token.tooltip}>
                      <Badge
                        size="xs"
                        color={token.isSubProperty ? "indigo" : "cap-blue"}
                        variant={token.isSubProperty ? "outline" : "light"}
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          // Insert into the active focused input
                          insertVariable(token.tokenKey, activeInput);
                        }}
                      >
                        {token.displayLabel}
                      </Badge>
                    </Tooltip>
                  ))}
                  {insertableTokens.length === 0 && (
                    <Text size="xs" color="dimmed">No custom fields in this template.</Text>
                  )}
                </Group>
              </div>

              <Divider my="md" label="Metadata options mappings (Optional)" labelPosition="center" />

              {/* Metadata Table */}
              {Object.keys(metadata).length > 0 && (
                <Table striped highlightOnHover mb="md">
                  <thead>
                    <tr>
                      <th>Key</th>
                      <th>Label</th>
                      <th>Template</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(metadata).map(([key, item]) => (
                      <tr key={key}>
                        <td>{key}</td>
                        <td>{item.label}</td>
                        <td>{item.template}</td>
                        <td>
                          <Button
                            size={isMobile ? "md" : "sm"}
                            color="red"
                            variant="outline"
                            onClick={() => handleRemoveMeta(key)}
                            style={{ minHeight: isMobile ? 44 : 36, minWidth: isMobile ? 44 : 36 }}
                            aria-label="Remove metadata"
                          >
                            <Trash size={isMobile ? 18 : 16} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}

              {/* New Metadata Inputs */}
              <Group spacing="xs" mb="md" align="flex-end">
                <TextInput
                  size="xs"
                  label="Metadata Key"
                  placeholder="e.g. rate"
                  value={metaKey}
                  onChange={(e) => setMetaKey(e.target.value)}
                  style={{ flex: 1 }}
                />
                <TextInput
                  size="xs"
                  label="Display Label"
                  placeholder="e.g. Hourly Rate"
                  value={metaLabel}
                  onChange={(e) => setMetaLabel(e.target.value)}
                  style={{ flex: 1 }}
                />
                <div style={{ flex: 2.2, display: "flex", gap: 8, alignItems: "flex-end" }}>
                  <div style={{ flex: 1 }}>
                    <TextInput
                      ref={metaRef}
                      size="xs"
                      label="Handlebars Template"
                      placeholder="e.g. {{pay_rate}}"
                      value={metaTemplate}
                      onChange={(e) => setMetaTemplate(e.target.value)}
                      onFocus={() => setActiveInput("meta")}
                    />
                  </div>
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <Text size="xs" weight={500} style={{ color: "#212529", marginBottom: 3 }}>
                      Fields
                    </Text>
                    <Button
                      ref={buttonRef}
                      size="sm"
                      variant="outline"
                      color="gray"
                      onClick={() => setVisible((v) => !v)}
                    >
                      Insert Field
                    </Button>
                    <Popper
                      arrowSize={5}
                      withArrow
                      mounted={visible}
                      referenceElement={buttonRef.current as HTMLButtonElement}
                      transition="pop-top-left"
                      transitionDuration={200}
                      withinPortal={false}
                      arrowStyle={{
                        backgroundColor:
                          theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[1],
                      }}
                    >
                      <div style={{ pointerEvents: "auto" }}>
                        <div ref={clickOutsideRef}>
                          <Paper
                            style={{
                              backgroundColor:
                                theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[1],
                              border: "1px solid #ced4da",
                              borderRadius: 4,
                              padding: 6,
                              boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                              zIndex: 1000,
                            }}
                          >
                            <ScrollArea
                              style={{
                                maxHeight: 220,
                                width: 180,
                              }}
                              scrollbarSize={4}
                            >
                              <div style={{ display: "flex", flexDirection: "column", gap: 6, padding: 2, alignItems: "flex-start" }}>
                                {insertableTokens.map((token) => (
                                  <Badge
                                    key={`m-${token.id}`}
                                    component="button"
                                    size="xs"
                                    color={token.isSubProperty ? "indigo" : "cap-blue"}
                                    variant={token.isSubProperty ? "outline" : "light"}
                                    style={{ cursor: "pointer" }}
                                    onClick={() => {
                                      insertVariable(token.tokenKey, "meta");
                                      setVisible(false);
                                    }}
                                  >
                                    {token.displayLabel}
                                  </Badge>
                                ))}
                                {insertableTokens.length === 0 && (
                                  <Text size="xs" color="dimmed" align="center" style={{ fontSize: "10px", padding: 2, width: "100%" }}>
                                    None
                                  </Text>
                                )}
                              </div>
                            </ScrollArea>
                          </Paper>
                        </div>
                      </div>
                    </Popper>
                  </div>
                </div>
                <Button
                  size="sm"
                  color="cap-blue"
                  variant="outline"
                  leftIcon={<Plus size={14} />}
                  disabled={!metaKey || !metaLabel || !metaTemplate}
                  onClick={handleAddMeta}
                >
                  Add Meta
                </Button>
              </Group>

              {/* Preview Area */}
              <Divider my="md" label="Builder Preview (Recent Submissions)" labelPosition="center" />

              {loadingSubmissions ? (
                <Group position="center" my="md">
                  <Loader size="sm" />
                  <Text size="sm">Evaluating templates...</Text>
                </Group>
              ) : previewError ? (
                <Alert color="yellow" icon={<InfoCircle size={16} />} mb="md">
                  {previewError}
                </Alert>
              ) : (
                <ScrollArea style={{ border: "1px solid #e0e0e0", borderRadius: 8, padding: 8, background: "#fafafa" }}>
                  <Text size="xs" color="dimmed" mb="xs" style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <Eye size={12} /> Showing up to 3 evaluated options from recent submissions.
                  </Text>
                  <Table striped highlightOnHover>
                    <thead>
                      <tr>
                        <th>Option Label</th>
                        <th>Option Value</th>
                        <th>Metadata Count</th>
                        <th>Metadata Preview</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewOptions.map((opt, idx) => (
                        <tr key={idx}>
                          <td style={{ fontWeight: 500 }}>{opt.label || <span style={{ color: "red", fontStyle: "italic" }}>Empty</span>}</td>
                          <td><code>{opt.value || <span style={{ color: "red", fontStyle: "italic" }}>Empty</span>}</code></td>
                          <td>{opt.metadata ? Object.keys(opt.metadata).length : 0}</td>
                          <td>
                            {opt.metadata ? (
                              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                                {Object.entries(opt.metadata).map(([k, v]) => (
                                  <Badge key={k} size="xs" variant="filled" color="indigo">
                                    {v.label || k}: {String(v.value)}
                                  </Badge>
                                ))}
                              </div>
                            ) : (
                              <Text size="xs" color="dimmed">None</Text>
                            )}
                          </td>
                        </tr>
                      ))}
                      {previewOptions.length === 0 && (
                        <tr>
                          <td colSpan={4} style={{ textAlign: "center", color: "orange" }}>
                            No options generated. Please check your mapping templates.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </ScrollArea>
              )}
            </>
          )}

          {buildError && (
            <Alert title="Builder Error" color="red" mt="md">
              {buildError}
            </Alert>
          )}

          <Space h="xl" />

          <Group position="right" mt="xl">
            <Button variant="default" color="gray" size="sm" style={{ minHeight: 36 }} onClick={onClose} disabled={building}>
              Cancel
            </Button>
            <Button
              color="cap-navy"
              size="sm"
              style={{ minHeight: 36 }}
              onClick={handleBuild}
              loading={building}
              disabled={
                building ||
                !name ||
                !selectedTemplateId ||
                !labelTemplate ||
                !valueTemplate
              }
            >
              Build Options Set
            </Button>
          </Group>
        </div>
      )}
    </Modal>
  );
}
